// src/pages/SupportPage.jsx
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
//import { sendSupportEmail } from "../utils/emailService";
import config from "../utils/config";
// Add import at top:
import apiConfig from "../utils/apiConfig";
import { saveSupportTicket } from "../utils/transactions";

const TOPICS = [
  "Payment Issue",
  "Analysis Not Working",
  "Download Problem",
  "Account / Login Issue",
  "Refund Request",
  "Technical Bug",
  "General Question",
  "Other",
];

export default function SupportPage() {
  const { user, userData } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: userData?.firstName || "",
    lastName: userData?.lastName || "",
    email: userData?.email || user?.email || "",
    topic: "",
    message: "",
  });

  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  function update(field) {
    return (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));
  }

async function handleSubmit(e) {
  e.preventDefault();
  setErrorMsg("");

  if (!form.firstName.trim()) { setErrorMsg("Please enter your first name."); return; }
  if (!form.lastName.trim()) { setErrorMsg("Please enter your last name."); return; }
  if (!form.email.trim()) { setErrorMsg("Please enter your email address."); return; }
  if (!form.topic) { setErrorMsg("Please select a topic."); return; }
  if (!form.message.trim() || form.message.trim().length < 20) {
    setErrorMsg("Please describe your issue in at least 20 characters.");
    return;
  }

  setStatus("sending");

  try {
    // Step 1 — Save to Firestore first to get ticketId
    const ticketId = await saveSupportTicket(user?.uid || null, {
      firstName: form.firstName,
      lastName:  form.lastName,
      email:     form.email,
      topic:     form.topic,
      message:   form.message,
    });

    console.log("✅ Ticket saved with ID:", ticketId);

    // // Step 2 — Send emails with ticketId
    // await sendSupportEmail(
    //   form.firstName,
    //   form.lastName,
    //   form.email,
    //   form.topic,
    //   form.message,
    //   user?.uid,
    //   ticketId,   // ← passing ticketId
    // );

        // Step 2 — Call backend directly (same pattern as PaymentModal)
    const res = await fetch(`${apiConfig.BACKEND_URL}/email/support`, {
      method:  "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: form.firstName,
        lastName:  form.lastName,
        email:     form.email,
        topic:     form.topic,
        message:   form.message,
        userId:    user?.uid || null,
        ticketId,
      }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || "Failed to send support email");
    }

    setStatus("success");
    setForm(prev => ({ ...prev, topic: "", message: "" }));

  } catch (err) {
    console.error("Support submit error:", err);
    setStatus("error");
    setErrorMsg("Failed to send. Please email us at " + config.SUPPORT_EMAIL);
  }


}

  return (
    <div style={{ minHeight: "100vh", background: "var(--gray-50)", display: "flex", flexDirection: "column" }}>

      {/* NAV */}
      <nav style={{ borderBottom: "1px solid var(--gray-200)", padding: "0 28px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", background: "#fff", position: "sticky", top: 0, zIndex: 100 }}>
        <Link to={user ? "/dashboard" : "/"} style={{ display: "flex", alignItems: "center", gap: 8, textDecoration: "none" }}>
          <div style={{ width: 30, height: 30, background: "var(--blue)", borderRadius: 7, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ color: "#fff", fontSize: 14 }}>📄</span>
          </div>
          <span style={{ fontWeight: 700, fontSize: 16, color: "var(--gray-900)" }}>
            ResumeScore<span style={{ color: "var(--blue)" }}>AI</span>
          </span>
        </Link>
        <div style={{ display: "flex", gap: 10 }}>
          {user ? (
            <button
              className="btn btn-outline"
              style={{ fontSize: 13, padding: "6px 14px" }}
              onClick={() => navigate("/dashboard")}
            >
              ← Back to Dashboard
            </button>
          ) : (
            <>
              <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={() => navigate("/login")}>Log In</button>
              <button className="btn btn-primary" style={{ fontSize: 13 }} onClick={() => navigate("/signup")}>Sign Up</button>
            </>
          )}
        </div>
      </nav>

      <div style={{ flex: 1, maxWidth: 640, margin: "0 auto", padding: "40px 20px", width: "100%" }}>

        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🎧</div>
          <h1 style={{ fontSize: 28, fontWeight: 700, letterSpacing: "-0.02em", marginBottom: 8 }}>
            How can we help?
          </h1>
          <p style={{ fontSize: 15, color: "var(--gray-500)", maxWidth: 420, margin: "0 auto" }}>
            Fill out the form below and we'll get back to you within 24 hours.
          </p>
        </div>

        {/* Success state */}
        {status === "success" ? (
          <div style={{ background: "#fff", border: "1px solid var(--gray-200)", borderRadius: 14, padding: "40px 32px", textAlign: "center", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
            <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8, letterSpacing: "-0.02em" }}>Message sent!</h2>
            <p style={{ fontSize: 14, color: "var(--gray-500)", marginBottom: 8 }}>
              Thank you for reaching out. We've received your message and will reply to{" "}
              <strong>{form.email}</strong> within 24 hours.
            </p>
            <p style={{ fontSize: 13, color: "var(--gray-400)", marginBottom: 28 }}>
              For urgent issues, email us directly at{" "}
              <a href={`mailto:${config.SUPPORT_EMAIL}`} style={{ color: "var(--blue)" }}>
                {config.SUPPORT_EMAIL}
              </a>
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
              <button className="btn btn-outline" onClick={() => setStatus("idle")}>
                Send Another Message
              </button>
              {user && (
                <button className="btn btn-primary" onClick={() => navigate("/dashboard")}>
                  Back to Dashboard
                </button>
              )}
            </div>
          </div>
        ) : (

          /* Form */
          <div style={{ background: "#fff", border: "1px solid var(--gray-200)", borderRadius: 14, padding: "32px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>

            {/* Quick contact info */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 24 }}>
              {[
                { icon: "📧", label: "Email us", value: config.SUPPORT_EMAIL, href: `mailto:${config.SUPPORT_EMAIL}` },
                { icon: "⏱️", label: "Response time", value: "Within 24 hours", href: null },
              ].map(item => (
                <div key={item.label} style={{ background: "var(--gray-50)", borderRadius: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ fontSize: 20 }}>{item.icon}</span>
                  <div>
                    <div style={{ fontSize: 11, color: "var(--gray-400)", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.04em" }}>{item.label}</div>
                    {item.href ? (
                      <a href={item.href} style={{ fontSize: 13, color: "var(--blue)", fontWeight: 500 }}>{item.value}</a>
                    ) : (
                      <div style={{ fontSize: 13, color: "var(--gray-700)", fontWeight: 500 }}>{item.value}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <form onSubmit={handleSubmit}>

              {/* Error message */}
              {errorMsg && (
                <div style={{ background: "#FCEBEB", color: "#A32D2D", fontSize: 13, padding: "10px 14px", borderRadius: 8, marginBottom: 20, border: "1px solid #f0a0a0" }}>
                  {errorMsg}
                </div>
              )}

              {/* First + Last name */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--gray-700)", marginBottom: 5 }}>
                    First name <span style={{ color: "#A32D2D" }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Jane"
                    value={form.firstName}
                    onChange={update("firstName")}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--gray-700)", marginBottom: 5 }}>
                    Last name <span style={{ color: "#A32D2D" }}>*</span>
                  </label>
                  <input
                    className="form-input"
                    type="text"
                    placeholder="Smith"
                    value={form.lastName}
                    onChange={update("lastName")}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--gray-700)", marginBottom: 5 }}>
                  Email address <span style={{ color: "#A32D2D" }}>*</span>
                </label>
                <input
                  className="form-input"
                  type="email"
                  placeholder="jane@example.com"
                  value={form.email}
                  onChange={update("email")}
                  required
                />
              </div>

              {/* Topic */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--gray-700)", marginBottom: 5 }}>
                  Topic <span style={{ color: "#A32D2D" }}>*</span>
                </label>
                <select
                  className="form-input"
                  value={form.topic}
                  onChange={update("topic")}
                  required
                  style={{ cursor: "pointer" }}
                >
                  <option value="">Select a topic...</option>
                  {TOPICS.map(t => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              {/* Message */}
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 500, color: "var(--gray-700)", marginBottom: 5 }}>
                  Message <span style={{ color: "#A32D2D" }}>*</span>
                </label>
                <textarea
                  className="form-input"
                  placeholder="Please describe your issue or question in detail..."
                  value={form.message}
                  onChange={update("message")}
                  required
                  rows={5}
                  style={{ resize: "vertical", lineHeight: 1.6 }}
                />
                <div style={{ fontSize: 12, color: form.message.length < 20 ? "var(--gray-400)" : "#3B6D11", marginTop: 4, textAlign: "right" }}>
                  {form.message.length} characters {form.message.length < 20 ? "(min 20)" : "✓"}
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="btn btn-primary btn-full btn-lg"
                disabled={status === "sending"}
                style={{ fontSize: 15, borderRadius: 10 }}
              >
                {status === "sending"
                  ? <><span className="spinner" style={{ width: 16, height: 16 }} /> Sending...</>
                  : "Send Message"
                }
              </button>

              <p style={{ fontSize: 12, color: "var(--gray-400)", textAlign: "center", marginTop: 12 }}>
                We typically respond within 24 hours on business days.
              </p>

            </form>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid var(--gray-200)", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", background: "#fff", flexWrap: "wrap", gap: 8 }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: "var(--gray-800)" }}>
          ResumeScore<span style={{ color: "var(--blue)" }}>AI</span>
        </span>
        <span style={{ fontSize: 13, color: "var(--gray-500)" }}>
          © {new Date().getFullYear()} ResumeScoreAI. All rights reserved.
        </span>
      </footer>

    </div>
  );
}